import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Inbox, RefreshCw, Mail, Phone, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { RequestNumber } from "@/components/RequestTracker";

const STATUSES = ["New", "Contacted", "Approved", "Declined"];
const statusTone = (s) => ({
  New: "text-blue-700 bg-blue-50 border-blue-200",
  Contacted: "text-amber-700 bg-amber-50 border-amber-200",
  Approved: "text-green-700 bg-green-50 border-green-200",
  Declined: "text-rose-700 bg-rose-50 border-rose-200",
}[s] || "text-slate-600 bg-slate-50 border-slate-200");

export default function ServiceRequests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/admin/service-requests").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const changeStatus = async (r, status) => {
    setBusyId(r.id);
    try {
      await api.put(`/admin/service-requests/${r.id}/status`, { status });
      setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, status } : x)));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const convert = async (r) => {
    if (!window.confirm(`Convert ${r.request_number} into a work order? The tracking number will be preserved.`)) return;
    setBusyId(r.id);
    try {
      const { data } = await api.post(`/admin/service-requests/${r.id}/convert`);
      toast.success(`Converted — Work Order ${data.wo_number}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  return (
    <DashboardLayout nav={getNav(user.role)} title="Service Requests">
      <div className="flex justify-end mb-4">
        <Button size="sm" variant="outline" data-testid="sr-refresh" onClick={load}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
      </div>
      {!rows ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> :
        rows.length === 0 ? <div className="text-center py-16 bg-white rounded-xl border border-slate-200"><Inbox className="w-10 h-10 text-slate-300 mx-auto" /><p className="mt-3 text-slate-500">No service requests yet.</p></div> : (
          <div className="space-y-3" data-testid="sr-list">
            {rows.map((r) => (
              <div key={r.id} data-testid={`sr-row-${r.id}`} className="bg-white border border-slate-200 rounded-xl p-4 md:p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <RequestNumber number={r.request_number} urgency={r.urgency} />
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${statusTone(r.status)}`} data-testid={`sr-status-badge-${r.id}`}>{r.status}</span>
                      <span className="text-xs text-slate-400">Urgency: {r.urgency}</span>
                    </div>
                    <div className="mt-2 font-medium text-slate-900">{r.name}</div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                      <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-slate-400" />{r.phone}</span>
                      <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-slate-400" />{r.email}</span>
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-slate-400" />{r.property_address}</span>
                    </div>
                    <div className="mt-2 text-sm"><span className="text-slate-400">Service:</span> <span className="text-slate-800 font-medium">{r.service_category}</span></div>
                    <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap">{r.problem_description}</p>
                    {r.additional_notes && <p className="mt-1 text-xs text-slate-500">Notes: {r.additional_notes}</p>}
                    <div className="mt-2 text-xs text-slate-400">Submitted {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}{r.requested_date ? ` · Preferred: ${r.requested_date}` : ""}</div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0 w-full sm:w-52">
                    <Select value={r.status} onValueChange={(v) => changeStatus(r, v)} disabled={busyId === r.id}>
                      <SelectTrigger data-testid={`sr-status-select-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    {r.work_order_id ? (
                      <Button variant="outline" data-testid={`sr-view-wo-${r.id}`} onClick={() => navigate(`/app/wo/${r.work_order_id}`)}>
                        <CheckCircle2 className="w-4 h-4 mr-1 text-green-600" />{r.wo_number || "View Work Order"}
                      </Button>
                    ) : (
                      <Button data-testid={`sr-convert-${r.id}`} disabled={busyId === r.id} onClick={() => convert(r)} className="bg-blue-600 hover:bg-blue-700">
                        {busyId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Convert to Work Order <ArrowRight className="w-4 h-4 ml-1" /></>}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </DashboardLayout>
  );
}
